import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface EditCommentModalProps {
  commentId: string
  currentBody: string
  onClose: () => void
  onSuccess: () => void
}

export default function EditCommentModal({
  commentId,
  currentBody,
  onClose,
  onSuccess,
}: EditCommentModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [commentBody, setCommentBody] = useState(currentBody)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Update form when props change
  useEffect(() => {
    setCommentBody(currentBody)
  }, [currentBody])

  // Focus textarea on mount
  useEffect(() => {
    bodyTextareaRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSubmitting])

  const updateComment = useMutation({
    mutationFn: async (body: string) => {
      if (!user) throw new Error('Not authenticated')

      const { error: commentError } = await supabase
        .from('comments')
        .update({
          body: body.trim(),
        })
        .eq('id', commentId)

      if (commentError) throw commentError
    },
    onSuccess: () => {
      // Invalidate comments query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['thread-comments'] })
      queryClient.invalidateQueries({ queryKey: ['group-threads'] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
      
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Basic validation
    if (!commentBody.trim()) {
      setError('Comment content is required')
      return
    }

    if (commentBody.trim().length < 3) {
      setError('Comment must be at least 3 characters')
      return
    }

    setIsSubmitting(true)
    try {
      await updateComment.mutateAsync(commentBody.trim())
    } catch (err: any) {
      setError(err.message || 'Failed to update comment')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-comment-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="edit-comment-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Edit Comment
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Comment Body Field */}
          <div>
            <label 
              htmlFor="edit-comment-body"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Comment
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <textarea
              ref={bodyTextareaRef}
              id="edit-comment-body"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Write your comment here..."
              rows={6}
              required
              minLength={3}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            />
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Minimum 3 characters required
            </p>
          </div>

          {error && (
            <div 
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSubmitting ? 'Updating...' : 'Update Comment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

